import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaFilter, FaCheck } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@/services/apiClient';

interface FormApprovalRequest {
  formApprovalRequestId: number;
  formId: number;
  formGuid?: string;
  guid: string;
  formSubmittedNtid?: string;
  formSubmittedFirstName?: string;
  formSubmittedLastName?: string;
  formSubmittedEmail?: string;
  formApprovalRequestTitle?: string;
  formApprovalResponse?: string;
  formApprovalRequestSentDate?: string;
  formApprovalResponseDate?: string;
  createDt: string;
  createUser: string;
  effEndDt?: string;
}

interface EditState {
  formSubmittedFirstName: string;
  formSubmittedLastName: string;
  formSubmittedEmail: string;
}

const FormApprovalRequests: React.FC = () => {
  const [searchParams] = useSearchParams();
  const formGuidFilter = searchParams.get('formGuid') ?? null;

  const [records, setRecords] = useState<FormApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editRecord, setEditRecord] = useState<FormApprovalRequest | null>(null);
  const [editState, setEditState] = useState<EditState>({ formSubmittedFirstName: '', formSubmittedLastName: '', formSubmittedEmail: '' });
  const [saving, setSaving] = useState(false);

  const [deleteRecord, setDeleteRecord] = useState<FormApprovalRequest | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<FormApprovalRequest[]>(
        `/api/admin/formapprovals?incompleteOnly=${incompleteOnly}`
      );
      setRecords(data);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [incompleteOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = records.filter(r => {
    if (formGuidFilter && r.formGuid !== formGuidFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.formApprovalRequestTitle ?? '').toLowerCase().includes(q) ||
      (r.formSubmittedNtid ?? '').toLowerCase().includes(q) ||
      (r.formSubmittedFirstName ?? '').toLowerCase().includes(q) ||
      (r.formSubmittedLastName ?? '').toLowerCase().includes(q) ||
      (r.formSubmittedEmail ?? '').toLowerCase().includes(q)
    );
  });

  const openEdit = (record: FormApprovalRequest) => {
    setEditRecord(record);
    setEditState({
      formSubmittedFirstName: record.formSubmittedFirstName ?? '',
      formSubmittedLastName: record.formSubmittedLastName ?? '',
      formSubmittedEmail: record.formSubmittedEmail ?? '',
    });
  };

  const handleSave = async () => {
    if (!editRecord) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(`/api/admin/formapprovals/${editRecord.guid}/submitter`, editState);
      setSuccess(`Record updated successfully.`);
      setEditRecord(null);
      await load();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRecord) return;
    setDeleting(true);
    setError(null);
    try {
      await apiClient.delete(`/api/admin/formapprovals/${deleteRecord.guid}`);
      setSuccess(`Record deleted successfully.`);
      setDeleteRecord(null);
      await load();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const isIncomplete = (r: FormApprovalRequest) =>
    !r.formSubmittedFirstName || !r.formSubmittedLastName || !r.formSubmittedEmail;

  const formatDate = (val?: string) =>
    val ? new Date(val).toLocaleString() : '—';

  return (
    <div className="container-fluid p-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Form Approval Requests</h4>
          <p className="text-muted mb-0">Review and fix incomplete submitter information</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Form.Check
            type="switch"
            id="incomplete-only-toggle"
            label={<><FaFilter className="me-1" />Incomplete only</>}
            checked={incompleteOnly}
            onChange={e => setIncompleteOnly(e.target.checked)}
          />
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <InputGroup className="mb-3" style={{ maxWidth: 400 }}>
        <InputGroup.Text><FaSearch /></InputGroup.Text>
        <Form.Control
          placeholder="Search by ID, title, name, or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <Button variant="outline-secondary" onClick={() => setSearch('')}>Clear</Button>
        )}
      </InputGroup>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <p className="text-muted small mb-2">Showing {filtered.length} of {records.length} records</p>
          <div className="table-responsive">
            <Table hover size="sm">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>NTID</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Response</th>
                  <th>Created</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      No records found
                    </td>
                  </tr>
                ) : (
                  filtered.map(r => (
                    <tr key={r.formGuid} className={isIncomplete(r) ? 'table-warning' : undefined}>
                      <td className="text-truncate" style={{ maxWidth: 220 }} title={r.formApprovalRequestTitle ?? ''}>
                        {r.formApprovalRequestTitle || <span className="text-muted fst-italic">—</span>}
                      </td>
                      <td>{r.formSubmittedNtid || <span className="text-muted fst-italic">—</span>}</td>
                      <td>
                        {r.formSubmittedFirstName
                          ? r.formSubmittedFirstName
                          : <Badge bg="warning" text="dark">Missing</Badge>}
                      </td>
                      <td>
                        {r.formSubmittedLastName
                          ? r.formSubmittedLastName
                          : <Badge bg="warning" text="dark">Missing</Badge>}
                      </td>
                      <td>
                        {r.formSubmittedEmail
                          ? r.formSubmittedEmail
                          : <Badge bg="warning" text="dark">Missing</Badge>}
                      </td>
                      <td>{r.formApprovalResponse || <span className="text-muted fst-italic">Pending</span>}</td>
                      <td>{formatDate(r.createDt)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Edit submitter info"
                            onClick={() => openEdit(r)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Delete record"
                            onClick={() => setDeleteRecord(r)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {/* Edit modal */}
      <Modal show={!!editRecord} onHide={() => setEditRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Submitter — {editRecord?.formApprovalRequestTitle ?? 'Record'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editRecord?.formApprovalRequestTitle && (
            <p className="text-muted mb-3"><small>{editRecord.formApprovalRequestTitle}</small></p>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={editState.formSubmittedFirstName}
                onChange={e => setEditState(s => ({ ...s, formSubmittedFirstName: e.target.value }))}
                placeholder="First name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={editState.formSubmittedLastName}
                onChange={e => setEditState(s => ({ ...s, formSubmittedLastName: e.target.value }))}
                placeholder="Last name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={editState.formSubmittedEmail}
                onChange={e => setEditState(s => ({ ...s, formSubmittedEmail: e.target.value }))}
                placeholder="submitter@enmax.com"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditRecord(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheck className="me-1" />}
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirm modal */}
      <Modal show={!!deleteRecord} onHide={() => setDeleteRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this record?
          {deleteRecord?.formApprovalRequestTitle && (
            <p className="text-muted mt-1 mb-0"><small>{deleteRecord.formApprovalRequestTitle}</small></p>
          )}
          <p className="text-danger mt-2 mb-0"><small>This action cannot be undone.</small></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteRecord(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" animation="border" className="me-1" /> : <FaTrash className="me-1" />}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FormApprovalRequests;
